import type { Employee, EmployeeStatus } from "@/lib/api";

/** Mirror of the server's EMPLOYEE_STATUSES (src/api/src/lib/employee-status.ts). */
export const EMPLOYEE_STATUSES: readonly EmployeeStatus[] = [
  "attested_pending_verification",
  "attestation_not_found",
  "paid",
  "directed_to_bim",
  "fingerprint_appointment",
  "background_cleared",
  "entered_in_systems",
  "badge_issued",
  "badge_delivered",
];

/** The one source of plain-language status wording for the client. Typed as a
 *  total Record, so a status added to EmployeeStatus fails typecheck here.
 *  `formatEmployeeStatus` (formatters.ts) reads this map; Plan 3's dashboard
 *  reuses it. */
export const STATUS_LABELS: Record<EmployeeStatus, string> = {
  attested_pending_verification: "Waiting for Clearinghouse verification",
  attestation_not_found: "Not found in Clearinghouse — action needed",
  paid: "Paid — awaiting background check start",
  directed_to_bim: "Sent to background check provider",
  fingerprint_appointment: "Fingerprint appointment scheduled",
  background_cleared: "Background check cleared",
  entered_in_systems: "Entered in diocesan systems",
  badge_issued: "Badge made",
  badge_delivered: "Badge delivered",
};

const BADGE_LIFE_YEARS = 5;

/** clearanceDate + 5 years as "YYYY-MM-DD", or null when unknown or
 *  unparseable. A leap day clamps to the last day of the month (Feb 29 -> Feb 28).
 *  Display-only: the server never computes expiry in v2, and a replacement
 *  never extends it. */
export function badgeExpiry(clearanceDate: string | undefined): string | null {
  if (!clearanceDate) return null;
  const parsed = new Date(clearanceDate);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getUTCFullYear() + BADGE_LIFE_YEARS;
  const month = parsed.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(parsed.getUTCDate(), lastDay);
  return new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
}

/** "2031-03-15" -> "Mar 15, 2031". Reads the date in UTC: a date-only value is
 *  a calendar date, and a local-time render would show the day before for any
 *  viewer west of UTC. */
export function formatCalendarDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export type RosterAction =
  | { kind: "replacement" }
  | { kind: "replacement_pending" }
  | { kind: "completion" }
  | { kind: "none" };

/** Shown where a second replacement would otherwise be offered. */
export const REPLACEMENT_PENDING_LABEL = "Replacement badge on order";

/** The spec's action table. Offboard ("Mark no longer employed") is available in
 *  ANY state and Reactivate when inactive — both handled outside this function.
 *  An inactive employee gets no purchase action: the server refuses them, and
 *  it refuses a second replacement while one is on order. */
export function availableAction(e: Employee): RosterAction {
  if (e.inactive) return { kind: "none" };
  switch (e.status) {
    case "badge_issued":
    case "badge_delivered":
      return e.replacementPending ? { kind: "replacement_pending" } : { kind: "replacement" };
    case "attestation_not_found":
      return { kind: "completion" };
    default:
      return { kind: "none" };
  }
}
